import { i18n } from '@modernloop/i18n';
import { getDateFnsLocale, getTZAbbr } from '@modernloop/shared/datetime';
import { intervalToDuration } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

export const formatTz = (date: Date, timeZone: string, formatString: string): string =>
  format(toZonedTime(date, timeZone), formatString, { timeZone, locale: getDateFnsLocale() });

export const renderTimeRangeWithDayMthHrMinTz = (startAt, endAt, tz: string): string =>
  `${formatTz(startAt, tz, 'EEEE')}, ${formatTz(startAt, tz, 'MMM')} ${formatTz(startAt, tz, 'd')}, ${formatTz(
    startAt,
    tz,
    'h:mm a'
  )} - ${formatTz(endAt, tz, 'h:mm a')} ${getTZAbbr(tz)}`;

export const renderInterviewerHours = (
  startAt,
  endAt,
  tz: string,
  showTzString = true,
  showDuration = false
): string => {
  let durationString = '';

  if (showDuration) {
    const duration = intervalToDuration({ start: new Date(startAt), end: new Date(endAt) });

    const hours = duration?.hours ?? 0;
    const minutes = duration?.minutes ?? 0;

    if (hours > 0 && minutes > 0) {
      durationString = `(${i18n.t('common:duration.hoursMinutesShort', { count: hours, minutes })})`;
    } else if (hours > 0) {
      durationString = `(${i18n.t('common:duration.hoursShort', { count: hours })})`;
    } else if (minutes > 0) {
      durationString = `(${i18n.t('common:duration.minutesShort', { count: minutes })})`;
    }
  }

  return `${formatTz(startAt, tz, 'h:mm a')} - ${formatTz(endAt, tz, 'h:mm a')} ${showTzString ? getTZAbbr(tz) : ''} ${
    showDuration ? durationString : ''
  }`;
};

export const renderTimeRangeWithDateDayMthHrMinTz = (startAt, endAt, tz: string): string =>
  `${formatTz(startAt, tz, 'EEEE')}, ${formatTz(startAt, tz, 'MMM')} ${formatTz(startAt, tz, 'd')}, ${formatTz(
    startAt,
    tz,
    'h:mm a'
  )} - ${formatTz(endAt, tz, 'EEEE')}, ${formatTz(endAt, tz, 'MMM')} ${formatTz(endAt, tz, 'd')}, ${formatTz(
    endAt,
    tz,
    'h:mm a'
  )} ${getTZAbbr(tz)}`;

export const renderTimeRange = (
  tz: string, // timezone is so important I want it first
  startAt: Date,
  endAt: Date
): string =>
  `${formatTz(startAt, tz, 'h:mm a')} - ${formatTz(endAt, tz, 'h:mm a')} ${getTZAbbr(tz)}, ${formatTz(
    endAt,
    tz,
    'EEE'
  )}`;

export const renderTimeRangeUnix = (
  tz: string, // timezone is so important I want it first
  startAt: number,
  endAt: number
): string => renderTimeRange(tz, new Date(startAt), new Date(endAt));

export const renderTimeRangeISO = (
  tz: string, // timezone is so important I want it first
  startAt: string,
  endAt: string
): string => renderTimeRange(tz, new Date(startAt), new Date(endAt));
